<template>
  <section
    id="book"
    class="i-pricing-section"
  >
    <div class="container">
      <div class="text-center">
        <div
          class="label mb-3"
          style="justify-content: center"
        >
          One-time. No subscription.
        </div>
        <h2 class="headline">${{ pricing }}. Yours. Done.</h2>
        <p
          class="body-lg"
          style="color: var(--text-body); max-width: 500px; margin: 12px auto 0"
        >
          Career coaches charge $250–$400 per hour. This is
          {{ timing }} minutes of that expertise, focused entirely on your next move.
        </p>
      </div>

      <div class="pricing__wrap">
        <div class="pricing__savings-badge">
          Save ${{ Math.round(parseInt(highPricing) - parseInt(pricing)) }} —
          {{ Math.round(((parseInt(highPricing) - parseInt(pricing)) * 100) / parseInt(highPricing)) }}% off today only
        </div>
        <div class="pricing__card">
          <div class="pricing__header">
            <div>
              <div class="pricing__headline">Career Strategy Session</div>
              <div class="pricing__anchor">Regular price: ${{ highPricing }}</div>
            </div>
            <div>
              <div class="pricing__amount-row">
                <div class="pricing__amount">${{ pricing }}</div>
                <div class="pricing__qualifier">one time · save 67%</div>
              </div>
            </div>
          </div>

          <div class="pricing__body">
            <div
              v-for="item in items"
              :key="item"
              class="pricing__item"
            >
              <div class="pricing__check">✓</div>
              <span>{{ item }}</span>
            </div>

            <div class="pricing__guarantee">
              <strong>100% money-back guarantee</strong>
              If you don't leave the session with at least 3 specific, actionable next steps you can take this week, we
              refund the full ${{ pricing }}. No questions, no process.
            </div>

            <div class="pricing__cta-wrap">
              <a
                class="pricing__btn"
                :class="{ disabled: paymentProcessing }"
                href="#"
                @click.prevent="$emit('book')"
                >{{ paymentProcessing ? "Processing..." : `Book my session — $${pricing}` }}</a
              >
              <div
                v-if="!paymentProcessing"
                class="skip"
                @click="$emit('skip')"
              >
                No thanks - I'm fine figuring it out the hard way
              </div>
              <div class="scarcity-line">
                We run 48 sessions per week. This week: <strong>11 slots remaining.</strong>
              </div>
              <div class="no-upsell-line">
                No upsell. No follow-up sales calls. You will never be pitched anything after this session. Ever.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"

defineEmits(["book", "skip"])

// PROPS
const props = defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  highPricing: {
    type: String,
    default: "149.90"
  },
  paymentProcessing: {
    type: Boolean,
    default: false
  }
})

// COMPUTED
const items = computed(() => [
  `${props.timing}-min 1:1 with a former recruiter or hiring manager — reviewed your report in advance`,
  "Written action plan delivered within 24 hours of the session",
  "Specific title targets and company shortlist tailored to your profile",
  "LinkedIn headline and positioning review with exact recommended language",
  "Skills gap identification — what's on your profile that's costing you interviews"
])
</script>

<style lang="scss" scoped>
.i-pricing-section {
  .pricing {
    &__wrap {
      position: relative;
      max-width: 520px;
      margin: 36px auto 0;
    }

    &__savings-badge {
      position: absolute;
      top: -14px;
      right: 20px;
      display: inline-block;
      font-size: 0.82rem;
      font-weight: 700;
      color: var(--green-dark);
      background: var(--green-lt);
      border: 1px solid #10b98140;
      border-radius: 8px;
      padding: 6px 14px;
      z-index: 2;
      box-shadow: 0 2px 8px #10b98126;
    }

    &__card {
      background: #fff;
      border: 2px solid var(--text);
      border-radius: var(--radius);
      overflow: hidden;
      box-shadow: 6px 6px 0 var(--text);
    }

    &__header {
      background: var(--navy-mid);
      padding: 24px 32px;
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }

    &__headline {
      font-size: 1.3rem;
      font-weight: 800;
      color: #f7f4ef;
    }

    &__anchor {
      font-size: 0.82rem;
      color: #f7f4ef73;
      text-decoration: line-through;
    }

    &__amount-row {
      display: flex;
      align-items: baseline;
      gap: 10px;
    }

    &__amount {
      font-size: 3.2rem;
      font-weight: 800;
      color: #f7f4ef;
      line-height: 1;
      letter-spacing: -0.02em;
    }

    &__qualifier {
      font-size: 0.8rem;
      color: #f7f4ef99;
    }

    &__body {
      padding: 28px 32px;
    }

    &__item {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 14px;
      font-size: 0.9rem;
      color: var(--text);
      line-height: 1.5;
    }

    &__check {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--green);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 800;
      margin-top: 1px;
    }

    &__guarantee {
      background: var(--green-lt);
      border: 1px solid #10b98140;
      border-radius: var(--radius-sm);
      padding: 14px 16px;
      margin: 20px 0;
      font-size: 0.875rem;
      color: var(--green-dark);
      font-weight: 500;
      line-height: 1.6;

      strong {
        display: block;
        margin-bottom: 2px;
        font-size: 0.875rem;
      }
    }

    &__cta-wrap {
      margin-top: 24px;

      .skip {
        margin-top: 6px;
        color: #c9c9c9;

        &:hover {
          color: #878787;
        }
      }
    }

    &__btn {
      width: 100%;
      background: var(--grad-primary);
      color: white;
      border: none;
      border-radius: var(--radius-pill);
      padding: 18px 32px;
      font-size: 1.05rem;
      font-weight: 600;
      cursor: pointer;
      text-decoration: none;
      transition:
        transform 0.15s,
        box-shadow 0.15s;
      display: block;
      text-align: center;
      line-height: 1;
      box-shadow: 0 8px 32px #6b5cff59;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px #6b5cff73;
      }

      &.disabled {
        transition: none;
        cursor: not-allowed;

        &:hover {
          transform: none;
          box-shadow: 0 8px 32px #6b5cff59;
        }
      }
    }
  }
}
</style>
